// LaTeX Editor JS Bundle — Entry Point
// Exposes window.LatexEditor API for Flutter dart:js_interop

import { EditorView } from '@codemirror/view';
import {
  createCodeMirrorEditor,
  setEditorContent,
  getEditorContent,
  setCursorPosition,
  insertAtCursor,
  wrapSelection,
  scrollToLine,
  getCursorLine,
  setVimMode,
  toggleCommentCommand,
  openSearchCommand,
  type EditorOptions,
} from './editor';
import {
  createCollaboration,
  setCurrentFile,
  getRandomUserColor,
  type CollabInstance,
  type CollabOptions,
  type CollabUser,
  type CollabUserState,
} from './collab';
import {
  createPdfViewer,
  type PdfViewerInstance,
  type PdfViewerOptions,
} from './pdf-viewer';
import {
  initSwiftLatex,
  compileLocally,
  isEngineReady,
  type CompileResult,
} from './swiftlatex';
import {
  parseSyncTex,
  forwardSync,
  inverseSync,
  type SyncTexData,
  type ForwardSyncResult,
  type InverseSyncResult,
} from './synctex-parser';

// Store instances by container ID
const editors = new Map<string, EditorView>();
const pdfViewers = new Map<string, PdfViewerInstance>();
const collabInstances = new Map<string, CollabInstance>();
const synctexDataStore = new Map<string, SyncTexData>();

// Callback registries
const contentChangeCallbacks = new Map<string, (content: string) => void>();
const cursorChangeCallbacks = new Map<string, (line: number, col: number) => void>();
const collabUsersCallbacks = new Map<string, (users: CollabUserState[]) => void>();
const compileStatusCallbacks = new Map<string, (status: string) => void>();
const inverseSyncCallbacks = new Map<string, (file: string, line: number) => void>();

// Global API exposed to Flutter via window.LatexEditor
const LatexEditor = {
  // ==========================================
  // Editor Management
  // ==========================================

  createEditor(containerId: string, initialContent: string = '', readOnly: boolean = false): boolean {
    try {
      const container = document.getElementById(containerId);
      if (!container) {
        console.error(`[LatexEditor] Container not found: ${containerId}`);
        return false;
      }

      // Clear existing editor
      if (editors.has(containerId)) {
        editors.get(containerId)!.destroy();
      }

      const options: EditorOptions = {
        initialContent,
        readOnly,
        onContentChange: (content: string) => {
          const cb = contentChangeCallbacks.get(containerId);
          if (cb) cb(content);
        },
        onCursorChange: (line: number, col: number) => {
          const cb = cursorChangeCallbacks.get(containerId);
          if (cb) cb(line, col);
        },
      };

      const view = createCodeMirrorEditor(container, options);
      editors.set(containerId, view);
      return true;
    } catch (e) {
      console.error('[LatexEditor] createEditor error:', e);
      return false;
    }
  },

  setContent(containerId: string, content: string): void {
    const view = editors.get(containerId);
    if (view) setEditorContent(view, content);
  },

  getContent(containerId: string): string {
    const view = editors.get(containerId);
    return view ? getEditorContent(view) : '';
  },

  setCursor(containerId: string, line: number, col: number): void {
    const view = editors.get(containerId);
    if (view) setCursorPosition(view, line, col);
  },

  insertText(containerId: string, text: string): void {
    const view = editors.get(containerId);
    if (view) insertAtCursor(view, text);
  },

  wrapSelection(containerId: string, before: string, after: string): void {
    const view = editors.get(containerId);
    if (view) wrapSelection(view, before, after);
  },

  destroyEditor(containerId: string): void {
    const view = editors.get(containerId);
    if (view) {
      view.destroy();
      editors.delete(containerId);
    }
    contentChangeCallbacks.delete(containerId);
    cursorChangeCallbacks.delete(containerId);
  },

  // ==========================================
  // Collaboration
  // ==========================================

  connectCollab(
    containerId: string,
    wsUrl: string,
    roomName: string,
    userName: string,
    userColor: string = '',
    userColorLight: string = '',
    token: string = ''
  ): boolean {
    try {
      // Disconnect existing if any
      LatexEditor.disconnectCollab(containerId);

      const colors = userColor
        ? { color: userColor, colorLight: userColorLight || userColor + '33' }
        : getRandomUserColor();

      const user: CollabUser = {
        name: userName,
        color: colors.color,
        colorLight: colors.colorLight,
      };

      const collabOptions: CollabOptions = {
        wsUrl,
        roomName,
        user,
        token: token || undefined,
        onUsersChange: (users: CollabUserState[]) => {
          const cb = collabUsersCallbacks.get(containerId);
          if (cb) cb(users);
        },
        onConnectionStatus: (status: string) => {
          const cb = compileStatusCallbacks.get(containerId);
          if (cb) cb(`collab:${status}`);
        },
      };

      const collab = createCollaboration(collabOptions);
      collabInstances.set(containerId, collab);

      // If editor already exists, we need to recreate it with collab extensions
      const existingView = editors.get(containerId);
      if (existingView) {
        const container = existingView.dom.parentElement;
        if (container) {
          const currentContent = getEditorContent(existingView);
          existingView.destroy();
          editors.delete(containerId);

          // Seed Y.Text from current editor buffer when empty (first joiner).
          const ytext = collab.ydoc.getText('codemirror');
          if (ytext.length === 0 && currentContent.length > 0) {
            ytext.insert(0, currentContent);
          }

          // Recreate editor with y-codemirror collab extensions bound.
          // Prefer Y.Text as source of truth when non-empty (remote already seeded).
          const initial =
            ytext.length > 0 ? ytext.toString() : currentContent;
          const view = createCodeMirrorEditor(container, {
            initialContent: initial,
            readOnly: false,
            extensions: collab.extensions,
            onContentChange: (content: string) => {
              const cb = contentChangeCallbacks.get(containerId);
              if (cb) cb(content);
            },
            onCursorChange: (line: number, col: number) => {
              const cb = cursorChangeCallbacks.get(containerId);
              if (cb) cb(line, col);
            },
          });

          editors.set(containerId, view);
        }
      }

      return true;
    } catch (e) {
      console.error('[LatexEditor] connectCollab error:', e);
      return false;
    }
  },

  disconnectCollab(containerId: string): void {
    const collab = collabInstances.get(containerId);
    if (collab) {
      collab.destroy();
      collabInstances.delete(containerId);
    }
    collabUsersCallbacks.delete(containerId);
  },

  setCollabFile(containerId: string, filePath: string): void {
    const collab = collabInstances.get(containerId);
    if (collab) {
      setCurrentFile(collab.provider, filePath);
    }
  },

  getCollabSnapshot(containerId: string): Uint8Array | null {
    const collab = collabInstances.get(containerId);
    return collab ? collab.getSnapshot() : null;
  },

  getCollabText(containerId: string): string {
    const collab = collabInstances.get(containerId);
    return collab ? collab.getText() : '';
  },

  // ==========================================
  // PDF Viewer
  // ==========================================

  createPdfViewer(containerId: string): boolean {
    try {
      const container = document.getElementById(containerId);
      if (!container) {
        console.error(`[LatexEditor] PDF container not found: ${containerId}`);
        return false;
      }

      // Destroy existing
      if (pdfViewers.has(containerId)) {
        pdfViewers.get(containerId)!.destroy();
      }

      const viewer = createPdfViewer(container, {
        onPageChange: (page, total) => {
          // Notify Flutter
          const cb = compileStatusCallbacks.get(containerId);
          if (cb) cb(`page:${page}/${total}`);
        },
        onError: (error) => {
          console.error('[PdfViewer]', error);
        },
        onSyncTexClick: (page, x, y) => {
          // Inverse SyncTeX: PDF click → source line
          const data = synctexDataStore.get(containerId);
          if (!data) return;
          const result = inverseSync(data, page, x, y);
          if (!result) return;

          // Try to scroll the editor to the line
          const view = editors.get(containerId);
          if (view) {
            scrollToLine(view, result.line);
          }

          // Notify Flutter (so it can switch files if needed)
          const cb = inverseSyncCallbacks.get(containerId);
          if (cb) cb(result.file, result.line);
        },
      });

      pdfViewers.set(containerId, viewer);
      return true;
    } catch (e) {
      console.error('[LatexEditor] createPdfViewer error:', e);
      return false;
    }
  },

  renderPdfFromBytes(containerId: string, data: Uint8Array): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) viewer.loadPdf(data);
  },

  renderPdfFromUrl(containerId: string, url: string): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) viewer.loadPdfUrl(url);
  },

  setPdfPage(containerId: string, page: number): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) viewer.setPage(page);
  },

  setPdfZoom(containerId: string, zoom: number): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) viewer.setZoom(zoom);
  },

  fitPdfWidth(containerId: string): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) viewer.fitWidth();
  },

  fitPdfPage(containerId: string): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) viewer.fitPage();
  },

  getPdfInfo(containerId: string): { page: number; total: number; zoom: number } | null {
    const viewer = pdfViewers.get(containerId);
    if (!viewer) return null;
    return {
      page: viewer.getCurrentPage(),
      total: viewer.getTotalPages(),
      zoom: viewer.getZoom(),
    };
  },

  destroyPdfViewer(containerId: string): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) {
      viewer.destroy();
      pdfViewers.delete(containerId);
    }
  },

  // ==========================================
  // SwiftLaTeX (Browser-side compilation)
  // ==========================================

  async initSwiftLatex(): Promise<boolean> {
    return initSwiftLatex({
      onReady: () => console.log('[LatexEditor] SwiftLaTeX ready'),
      onError: (err) => console.warn('[LatexEditor] SwiftLaTeX init error:', err),
    });
  },

  async compileLocal(
    mainContent: string,
    files: { path: string; content: string }[] = []
  ): Promise<CompileResult> {
    return compileLocally(mainContent, files);
  },

  isSwiftLatexReady(): boolean {
    return isEngineReady();
  },

  // ==========================================
  // Vim Mode
  // ==========================================

  setVimMode(containerId: string, enabled: boolean): void {
    const view = editors.get(containerId);
    if (view) setVimMode(view, enabled);
  },

  toggleComment(containerId: string): void {
    const view = editors.get(containerId);
    if (view) toggleCommentCommand(view);
  },

  openSearch(containerId: string): void {
    const view = editors.get(containerId);
    if (view) openSearchCommand(view);
  },

  // ==========================================
  // SyncTeX (bidirectional source ↔ PDF sync)
  // ==========================================

  /**
   * Load SyncTeX data from base64-encoded .synctex.gz content.
   * Must be called after each successful compilation to enable sync.
   */
  loadSynctex(containerId: string, base64Data: string): boolean {
    try {
      const data = parseSyncTex(base64Data);
      synctexDataStore.set(containerId, data);
      console.log(`[LatexEditor] SyncTeX loaded: ${data.entries.length} entries, ${data.inputFiles.size} files`);
      return true;
    } catch (e) {
      console.error('[LatexEditor] loadSynctex error:', e);
      return false;
    }
  },

  /**
   * Forward sync: source file + line → PDF page + position.
   * Returns { page, y, height } or null if no match.
   */
  forwardSync(containerId: string, file: string, line: number): ForwardSyncResult | null {
    const data = synctexDataStore.get(containerId);
    if (!data) return null;
    return forwardSync(data, file, line);
  },

  /**
   * Inverse sync: PDF page + coordinates → source file + line.
   * Returns { file, line } or null if no match.
   */
  inverseSync(containerId: string, page: number, x: number, y: number): InverseSyncResult | null {
    const data = synctexDataStore.get(containerId);
    if (!data) return null;
    return inverseSync(data, page, x, y);
  },

  /**
   * Forward sync from current cursor position.
   * Scrolls PDF to the corresponding position and highlights it.
   */
  syncEditorToPdf(containerId: string, file: string): boolean {
    const view = editors.get(containerId);
    const viewer = pdfViewers.get(containerId);
    const data = synctexDataStore.get(containerId);
    if (!view || !viewer || !data) return false;

    const line = getCursorLine(view);
    const result = forwardSync(data, file, line);
    if (!result) return false;

    viewer.highlightPosition(result.page, result.y, result.height);
    return true;
  },

  /**
   * Scroll editor to a specific line (used for inverse sync from Flutter).
   */
  scrollToLine(containerId: string, line: number): void {
    const view = editors.get(containerId);
    if (view) scrollToLine(view, line);
  },

  /**
   * Get current cursor line number (1-indexed).
   */
  getCursorLine(containerId: string): number {
    const view = editors.get(containerId);
    return view ? getCursorLine(view) : 0;
  },

  /**
   * Highlight a position in the PDF viewer (forward sync visual).
   */
  highlightPdfPosition(containerId: string, page: number, y: number, height: number): void {
    const viewer = pdfViewers.get(containerId);
    if (viewer) viewer.highlightPosition(page, y, height);
  },

  // ==========================================
  // Callbacks (called from Flutter via JS interop)
  // ==========================================

  onContentChange(containerId: string, callback: (content: string) => void): void {
    contentChangeCallbacks.set(containerId, callback);
  },

  onCursorChange(containerId: string, callback: (line: number, col: number) => void): void {
    cursorChangeCallbacks.set(containerId, callback);
  },

  onCollabUsersChange(containerId: string, callback: (users: CollabUserState[]) => void): void {
    collabUsersCallbacks.set(containerId, callback);
  },

  onStatusChange(containerId: string, callback: (status: string) => void): void {
    compileStatusCallbacks.set(containerId, callback);
  },

  onInverseSync(containerId: string, callback: (file: string, line: number) => void): void {
    inverseSyncCallbacks.set(containerId, callback);
  },

  // ==========================================
  // Cleanup
  // ==========================================

  destroyAll(containerId: string): void {
    LatexEditor.destroyEditor(containerId);
    LatexEditor.disconnectCollab(containerId);
    LatexEditor.destroyPdfViewer(containerId);
    synctexDataStore.delete(containerId);
    inverseSyncCallbacks.delete(containerId);
  },
};

// Expose to window for Flutter dart:js_interop
(window as any).LatexEditor = LatexEditor;

export default LatexEditor;
